<p class="section-description">
  Every block below is a real backend payload run through <code>toPresentedError</code>, so this is exactly what
  production renders. One anatomy throughout: the title says what failed, the detail says the consequence and what is
  still safe, the action is the remedy, and the reference line keeps the verbatim backend string.
  <strong>{{ totalCases }}</strong> cases previewed.
</p>
<p class="section-description">
  <strong>Trigger</strong> replays a case through the real <code>UiService</code>, so the router, the shell stack and
  the duplicate-collapse rule all take part and you see it arrive live. <strong>Play all</strong> fires a group in
  sequence — firing them at once would be capped at three and deduped, which hides the arrival behavior. Tiers 1 to 3
  render in their real hosts, so a trigger that reaches the corner instead means the wiring is broken. Tiers 4 and 5 are
  not previewed yet: neither has a host, and a block shown with nowhere to land reads as finished work.
</p>

<h3 class="modal-group-title subsection-toggle" (click)="toggleSubsection('tier1')">
  @if (openSubsections['tier1']) {
    <svg lucideChevronUp></svg>
  } @else {
    <svg lucideChevronDown></svg>
  }
  Tier 1 — Inputs
  <button
    type="button"
    class="arpio-secondary-button error-trigger"
    (click)="$event.stopPropagation(); resetAllInputs()"
  >
    Clear all
  </button>
</h3>
@if (openSubsections['tier1']) {
  <p class="section-description error-tier-rule">
    Every validated input in the app, in context, with its real validators — each one already showing the error state it
    can reach. The input that caused a failure owns the message: it renders under the control, marks it invalid so
    submit disables, and clears as soon as you type.
  </p>
  <p class="section-description">
    <strong>Re-trigger</strong> fires the case again live. Red-outlined ones send a real 422 through
    <code>UiService</code>; the form registers <code>arpFieldErrorHost</code> for every backend field, so
    <strong>if one produces a toast the field is unmapped, and that is the bug.</strong> The rest rewrite a value the
    control's own validator rejects. <strong>Clear</strong> empties a preview so you can watch it come back.
  </p>

  <form
    class="input-catalogue"
    [formGroup]="inputForm"
    [arpFieldErrorHost]="inputForm"
    [fieldErrorMap]="inputFieldMap"
    fieldErrorHostId="showcase-input-catalogue"
  >
    @for (preview of inputPreviews; track inputTrackBy($index, preview)) {
      <div class="input-case" [class.is-backend]="preview.mode === 'backend'">
        <div class="input-case-head">
          <label class="form-label" [attr.for]="'showcase-' + preview.key">{{ preview.label }}</label>
          <span class="input-case-context">{{ preview.context }}</span>
        </div>

        @if (preview.kind === 'textarea') {
          <textarea
            [id]="'showcase-' + preview.key"
            class="arpio-input code small full-width"
            rows="2"
            spellcheck="false"
            [formControlName]="preview.controlName"
            [placeholder]="preview.placeholder"
          ></textarea>
        } @else {
          <input
            [id]="'showcase-' + preview.key"
            type="text"
            class="arpio-input full-width"
            [formControlName]="preview.controlName"
            [placeholder]="preview.placeholder"
          />
        }

        <arp-field-issue
          [control]="inputForm.get(preview.controlName)"
          [message]="localMessage(preview)"
        ></arp-field-issue>

        <div class="input-case-triggers">
          <span class="input-case-note">{{ preview.note }}</span>
          <button
            type="button"
            class="arpio-secondary-button error-trigger"
            [class.is-backend]="preview.mode === 'backend'"
            (click)="triggerInput(preview)"
          >
            {{ preview.triggerLabel }}
          </button>
          <button type="button" class="arpio-secondary-button error-trigger is-reset" (click)="clearInput(preview)">
            Clear
          </button>
        </div>
      </div>
    }
  </form>
}

<h3 class="modal-group-title subsection-toggle" (click)="toggleSubsection('tier2')">
  @if (openSubsections['tier2']) {
    <svg lucideChevronUp></svg>
  } @else {
    <svg lucideChevronDown></svg>
  }
  Tier 2 — Dialogs
</h3>
@if (openSubsections['tier2']) {
  <p class="section-description error-tier-rule">
    Anything submitted from a modal renders inside that modal, above its footer, so the button that caused the failure
    is still visible. Structured failures render as structure, and the primary action disables with a stated reason
    rather than failing again on the next click.
  </p>
  <p class="section-description">
    Each frame below is the real anatomy in a dialog shape, already in its error state.
    <strong>Open in a real modal</strong> proves the routing end to end: it opens an actual <code>modal-dialog</code>,
    which mounts <code>arpSurfaceErrorHost</code>, then fires that case's real payload — so it is claimed
    <em>inside the dialog</em> rather than as a corner toast.
  </p>

  <!-- A real modal. Opening it mounts the surface host, so the error it fires is claimed in
       the dialog rather than the corner toast. -->
  <modal-dialog #liveDialogModal [dialogTemplate]="liveDialogTpl"></modal-dialog>
  <ng-template #liveDialogTpl>
    <div class="flex-center-column full-width">
      <div
        class="light-content-panel light-control-host live-dialog-panel"
        arpSurfaceErrorHost="showcase-live-dialog"
        #liveDialog="surfaceErrorHost"
      >
        <div class="dialog-frame-head">
          <span class="dialog-frame-title">{{ liveDialogPreview?.title }}</span>
          <span class="input-case-context">{{ liveDialogPreview?.context }}</span>
        </div>
        <div class="dialog-frame-body">{{ liveDialogPreview?.body }}</div>

        @if (liveDialog.error) {
          <arp-error-block
            class="dialog-error"
            [error]="liveDialog.error"
            [dismissible]="true"
            (dismissed)="liveDialog.clear()"
          ></arp-error-block>
        } @else {
          <span class="anchor-demo-empty">Waiting for the error to arrive…</span>
        }

        <div class="dialog-frame-foot">
          <span class="dialog-frame-hint">{{ liveDialog.error ? liveDialogPreview?.hint : '' }}</span>
          <button type="button" class="arpio-secondary-button" (click)="closeLiveDialog()">CLOSE</button>
          <button type="button" class="arpio-button" disabled>{{ liveDialogPreview?.submitLabel }}</button>
        </div>
      </div>
    </div>
  </ng-template>

  <div class="dialog-grid">
    @for (preview of dialogPreviews; track dialogTrackBy($index, preview)) {
      <div class="dialog-frame">
        <div class="dialog-frame-head">
          <span class="dialog-frame-title">{{ preview.title }}</span>
          <span class="input-case-context">{{ preview.context }}</span>
        </div>
        <div class="dialog-frame-body">{{ preview.body }}</div>

        <arp-error-block class="dialog-error" [error]="preview.error"></arp-error-block>

        <div class="dialog-frame-foot">
          <span class="dialog-frame-hint">{{ preview.hint }}</span>
          <button type="button" class="arpio-secondary-button" disabled>CANCEL</button>
          <button type="button" class="arpio-button" disabled>{{ preview.submitLabel }}</button>
        </div>
        <div class="dialog-frame-actions">
          <span class="error-cell-note">{{ preview.note }}</span>
          <button
            type="button"
            class="arpio-secondary-button error-trigger is-backend"
            (click)="openLiveDialog(preview)"
          >
            Open in a real modal
          </button>
        </div>
      </div>
    }
  </div>
}

@for (tier of tiers; track tierTrackBy($index, tier)) {
  <h3 class="modal-group-title subsection-toggle" (click)="toggleSubsection(tier.key)">
    @if (openSubsections[tier.key]) {
      <svg lucideChevronUp></svg>
    } @else {
      <svg lucideChevronDown></svg>
    }
    {{ tier.title }}
    <button
      type="button"
      class="arpio-secondary-button error-play"
      (click)="$event.stopPropagation(); play(tier.key, tier.entries)"
    >
      @if (playingKey === tier.key) {
        Playing…
      } @else {
        Play all ({{ tier.entries.length }})
      }
    </button>
  </h3>
  @if (openSubsections[tier.key]) {
    <p class="section-description error-tier-rule">{{ tier.rule }}</p>
    @if (tier.key === 'tier3') {
      <p class="section-description">
        <strong>Trigger</strong> fires into a real <code>arpObjectErrorHost</code> mounted below, so each case is
        claimed by the panel or the row that owns it.
        <strong>A Tier 3 trigger that lands in the corner instead means the wiring is broken.</strong> The frames are
        stand-in chrome; the hosts, the blocks and the routing are production.
      </p>
      <div class="object-grid">
        <div class="object-frame">
          <div class="object-frame-head">
            <div class="object-frame-ident">
              <span class="object-frame-title">app-prod-web</span>
              <span class="object-frame-sub">218167711420 / us-east-1 &rarr; us-west-2</span>
            </div>
            <span class="object-frame-pill">Standby</span>
          </div>

          <div class="object-tabs">
            <span class="object-tab is-active">Backup</span>
            <span class="object-tab">Recovery Environment</span>
            <span class="object-tab">All Issues</span>
            <span class="object-frame-controls">
              <button type="button" class="arpio-button">Test</button>
              <button type="button" class="arpio-danger-button">Launch Recovery</button>
            </span>
          </div>

          <div
            [arpObjectErrorHost]="objectPanelHost"
            [errorHostActions]="panelHostActions"
            #panelError="objectErrorHost"
          >
            @if (panelError.error) {
              <arp-error-block
                [error]="panelError.error"
                [dismissible]="true"
                (actionInvoked)="onPreviewAction(panelError)"
                (dismissed)="panelError.clear()"
              ></arp-error-block>
            } @else {
              <p class="object-frame-empty">Nothing blocking this application. Trigger a panel case below.</p>
            }
          </div>
        </div>

        <div class="object-frame">
          <div class="object-frame-head">
            <div class="object-frame-ident">
              <span class="object-frame-title">Row errors</span>
              <span class="object-frame-sub">any table whose row owns the failure</span>
            </div>
            <code class="object-frame-host">{{ objectRowHost }}</code>
          </div>

          <table class="object-rows">
            <thead>
              <tr>
                <th>Item</th>
                <th>Updated</th>
                <th>Status</th>
                <th aria-label="Actions"></th>
              </tr>
            </thead>
            <tbody [arpObjectErrorHost]="objectRowHost" [errorHostActions]="rowHostActions" #rowError="objectErrorHost">
              <tr [class.is-source]="rowError.error">
                <td class="object-rows-id">item_44e1</td>
                <td>15:38</td>
                <td><span class="object-frame-pill is-busy">In progress</span></td>
                <td class="object-rows-action">Retry</td>
              </tr>
              @if (rowError.error) {
                <tr class="object-rows-error">
                  <td colspan="4">
                    <div class="object-rows-nest">
                      <arp-error-block
                        [error]="rowError.error"
                        variant="row"
                        [dismissible]="true"
                        (dismissed)="rowError.clear()"
                      ></arp-error-block>
                    </div>
                  </td>
                </tr>
              }
              <tr>
                <td class="object-rows-id">item_44d7</td>
                <td>14:02</td>
                <td><span class="object-frame-pill is-failed">Failed</span></td>
                <td class="object-rows-action">Retry</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    }
    <div class="error-grid" [class.error-grid--compact]="tier.key === 'tier3'">
      @for (entry of tier.entries; track entryTrackBy($index, entry)) {
        <div class="error-cell">
          <div class="error-cell-meta">
            <span class="error-cell-label">{{ entry.label }}</span>
            <span class="error-cell-note">{{ entry.note }}</span>
          </div>
          @if (tier.key !== 'tier3') {
            <arp-error-block [error]="entry.error" [variant]="entry.variant"></arp-error-block>
          }
          <div class="error-cell-foot">
            <code class="error-cell-payload">{{ entry.payload }}</code>
            <button type="button" class="arpio-secondary-button error-trigger" (click)="trigger(entry)">Trigger</button>
          </div>
        </div>
      }
    </div>
  }
}

<h2 class="reference-divider">Reference</h2>
<p class="section-description">
  Not tiers — the anatomy and container options every tier above is built from, plus the bare code map.
</p>

<h3 class="modal-group-title subsection-toggle" (click)="toggleSubsection('severities')">
  @if (openSubsections['severities']) {
    <svg lucideChevronUp></svg>
  } @else {
    <svg lucideChevronDown></svg>
  }
  Four severities
  <button
    type="button"
    class="arpio-secondary-button error-play"
    (click)="$event.stopPropagation(); play('severities', severities)"
  >
    @if (playingKey === 'severities') {
      Playing…
    } @else {
      Play all
    }
  </button>
</h3>
@if (openSubsections['severities']) {
  <p class="section-description">
    Chip and buttons inherit the block's severity. Teal is the primary-action color and is never a failure.
  </p>
  <div class="error-grid">
    @for (entry of severities; track entryTrackBy($index, entry)) {
      <div class="error-cell">
        <div class="error-cell-meta">
          <span class="error-cell-label">{{ entry.label }}</span>
          <span class="error-cell-note">{{ entry.note }}</span>
        </div>
        <arp-error-block [error]="entry.error" [variant]="entry.variant"></arp-error-block>
        <div class="error-cell-foot">
          <code class="error-cell-payload">{{ entry.payload }}</code>
          <button type="button" class="arpio-secondary-button error-trigger" (click)="trigger(entry)">Trigger</button>
        </div>
      </div>
    }
  </div>
}

<h3 class="modal-group-title subsection-toggle" (click)="toggleSubsection('variants')">
  @if (openSubsections['variants']) {
    <svg lucideChevronUp></svg>
  } @else {
    <svg lucideChevronDown></svg>
  }
  Variants
  <button
    type="button"
    class="arpio-secondary-button error-play"
    (click)="$event.stopPropagation(); play('variants', variants)"
  >
    @if (playingKey === 'variants') {
      Playing…
    } @else {
      Play all
    }
  </button>
</h3>
@if (openSubsections['variants']) {
  <p class="section-description">
    Same anatomy in all four; only the container chrome changes, so a host can reframe the block without redefining any
    colors. Triggering these all render as the toast variant, since the shell is the only host that exists yet.
  </p>
  <div class="error-grid">
    @for (entry of variants; track entryTrackBy($index, entry)) {
      <div class="error-cell">
        <div class="error-cell-meta">
          <code class="error-cell-label">{{ entry.label }}</code>
          <span class="error-cell-note">{{ entry.note }}</span>
        </div>
        <arp-error-block [error]="entry.error" [variant]="entry.variant"></arp-error-block>
        <div class="error-cell-foot">
          <code class="error-cell-payload">{{ entry.payload }}</code>
          <button type="button" class="arpio-secondary-button error-trigger" (click)="trigger(entry)">Trigger</button>
        </div>
      </div>
    }
  </div>
}

<h3 class="modal-group-title subsection-toggle" (click)="toggleSubsection('codeMap')">
  @if (openSubsections['codeMap']) {
    <svg lucideChevronUp></svg>
  } @else {
    <svg lucideChevronDown></svg>
  }
  Bare HTTP code map
  <button
    type="button"
    class="arpio-secondary-button error-play"
    (click)="$event.stopPropagation(); play('codeMap', codeMap)"
  >
    @if (playingKey === 'codeMap') {
      Playing…
    } @else {
      Play all
    }
  </button>
</h3>
@if (openSubsections['codeMap']) {
  <p class="section-description">
    What reaches the user when the API returns a status and no body, so no substring matcher fires. Easy to regress and
    hard to notice.
  </p>
  <div class="error-grid error-grid--compact">
    @for (entry of codeMap; track entryTrackBy($index, entry)) {
      <div class="error-cell">
        <div class="error-cell-meta">
          <code class="error-cell-label">{{ entry.label }}</code>
          <span class="error-cell-note">{{ entry.note }}</span>
        </div>
        <arp-error-block [error]="entry.error" [variant]="entry.variant"></arp-error-block>
        <div class="error-cell-foot">
          <button type="button" class="arpio-secondary-button error-trigger" (click)="trigger(entry)">Trigger</button>
        </div>
      </div>
    }
  </div>
}

@if (playingKey) {
  <button type="button" class="arpio-secondary-button error-stop" (click)="stop()">Stop playback</button>
}

<!-- The production invite-user dialog. Its two cases open this rather than a look-alike, so
     the error is claimed by the host that really ships. -->
<app-invite-users-modal #realInviteUsersModal></app-invite-users-modal>

<!-- The production delete confirmation, the shape behind all nine destructive dialogs. Its
     cases open this so the failure is claimed by the host that really ships. -->
<modal-dialog #realConfirmActionModal [dialogTemplate]="realConfirmActionTpl"></modal-dialog>
<ng-template #realConfirmActionTpl>
  <confirm-action-dialog
    action="Delete"
    [name]="confirmActionPreview?.body"
    dialogId="showcase-delete-confirm"
    (onCancel)="closeConfirmActionDialog()"
    (onAction)="closeConfirmActionDialog()"
  >
    <div class="error margin-top">{{ confirmActionPreview?.context }}</div>
  </confirm-action-dialog>
</ng-template>

<!-- The four dialogs whose submits close only on success, so a failure lands in a dialog that
     is still on screen. Each opens with the mock application its candidate list filters on. -->
<modal-dialog #realConcludeRecoveryModal [dialogTemplate]="realConcludeRecoveryTpl"></modal-dialog>
<ng-template #realConcludeRecoveryTpl>
  <conclude-recovery-dialog
    [application]="mockApplication"
    [currentlySyncing]="false"
    (cancel)="realConcludeRecoveryModal.close()"
    (conclude)="realConcludeRecoveryModal.close()"
  ></conclude-recovery-dialog>
</ng-template>

<modal-dialog #realConcludeTestModal [dialogTemplate]="realConcludeTestTpl"></modal-dialog>
<ng-template #realConcludeTestTpl>
  <conclude-test-dialog
    [application]="mockApplication"
    (cancel)="realConcludeTestModal.close()"
    (conclude)="realConcludeTestModal.close()"
  ></conclude-test-dialog>
</ng-template>

<modal-dialog #realLaunchTestModal [dialogTemplate]="realLaunchTestTpl"></modal-dialog>
<ng-template #realLaunchTestTpl>
  <launch-test-dialog
    [application]="mockApplication"
    [currentlySyncing]="false"
    [launching]="false"
    (cancel)="realLaunchTestModal.close()"
    (launch)="realLaunchTestModal.close()"
  ></launch-test-dialog>
</ng-template>

<modal-dialog #realEditIntegrationModal [dialogTemplate]="realEditIntegrationTpl"></modal-dialog>
<ng-template #realEditIntegrationTpl>
  <edit-integration-dialog
    [integration]="null"
    [saving]="false"
    (cancel)="realEditIntegrationModal.close()"
    (save)="realEditIntegrationModal.close()"
  ></edit-integration-dialog>
</ng-template>
